import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    getStaffQuarterPriorityList,
    getStaffQuarterQuarterTypes,
    getEmployeeDetailsForStaffQuarter,
    generateStaffQuarterWillingness,
    getStaffQuarterPools,
    getStaffQuarterCurrentRegistration,
    getStaffQuarterLocations,
    getUserStaffQuarterRequests,
    getStaffQuarterDetails
} from "../../../../core/data/redux/staffQuarterSlice";
import { toast } from "react-toastify";
import { Modal, Select } from "antd";
import { resolveFlatCategory } from "../staffQuarterUtils";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import Table from "../../../../core/common/dataTable/index";
import { encodeRouteParam } from "../../../../core/hooks/routeEncoder";

/* ================= TYPES ================= */
interface PriorityRecord {
    key: string;
    priorityNo: number;
    empNo: string;
    name: string;
    designation: string;
    department: string;
    regDate: string;
    category: string;
    status: string;
    quarterType: string;
    location: string | string[];
    locationCode?: string;
    willingnessReferenceNumber?: string;
    willingnessStatus?: string;
    isWillingnessGenerated?: boolean;
    locationPreference1: string;
    locationPreference2: string;
    locationPreference3: string;
    registrationReferenceNumber: string;
    pool: string;
    dateOfBirth: string;
    dateOfJoining: string;
    begda: string;
    endDate: string;
}

const getPoolName = (item: any): string => {
    if (!item) return "";
    if (typeof item.pool === "string") return item.pool;
    if (item.pool && typeof item.pool === "object") {
        return item.pool.name || item.pool.poolName || item.pool.namePool || item.pool.type || item.pool.poolType || "";
    }
    return item.poolType || item.pool_type || item.poolName || item.pool_name || "";
};

const getPoolId = (item: any): string => {
    if (!item) return "";
    if (item.pool && typeof item.pool === "object") {
        const pObjId = item.pool.id || item.pool.poolTypeId || item.pool.pool_type_id || item.pool.poolId || item.pool.pool_id;
        if (pObjId != null) return String(pObjId);
    }
    const id = item.poolTypeId || item.pool_type_id || item.poolId || item.pool_id;
    return id != null ? String(id) : "";
};

const EmployeePriorityList = () => {
    const routes = all_routes;
    const dispatch = useDispatch<any>();


    const staffQuarterState = useSelector((state: any) => state.staffQuarterState);
    const employeeDetails = staffQuarterState?.employeeDetails || {};
    const currentRegistration = staffQuarterState?.currentRegistration?.data || {};
    const userRequests = staffQuarterState?.requests?.data || staffQuarterState?.requests || [];
    const currentRequest = staffQuarterState?.currentRequest?.data || staffQuarterState?.currentRequest || {};

    const isRegistered = useMemo(() => {
        return !!(currentRegistration && Object.keys(currentRegistration).length > 0 && (currentRegistration.id || currentRegistration.referenceNumber || currentRegistration.registrationId));
    }, [currentRegistration]);

    const [apiData, setApiData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [quarterTypes, setQuarterTypes] = useState<any[]>([]);

    const masterLocs = useMemo(() => {
        const reduxLocs = staffQuarterState?.locations?.data || staffQuarterState?.locations || [];
        const empLocs = employeeDetails?.locations || [];
        return reduxLocs.length > 0 ? reduxLocs : empLocs;
    }, [staffQuarterState?.locations, employeeDetails?.locations]);

    // Eligible options derived from employee details and master locations list
    const eligibleLocations = useMemo(() => {
        const prefIds = new Set<string>();
        const prefNames = new Set<string>();

        const processRegistrationObj = (obj: any) => {
            if (!obj) return;
            // 1. Direct IDs
            if (obj.locationPreference1Id) prefIds.add(String(obj.locationPreference1Id));
            if (obj.locationPreference2Id) prefIds.add(String(obj.locationPreference2Id));
            if (obj.locationPreference3Id) prefIds.add(String(obj.locationPreference3Id));
            if (obj.locationPreference1_id) prefIds.add(String(obj.locationPreference1_id));
            if (obj.locationPreference2_id) prefIds.add(String(obj.locationPreference2_id));
            if (obj.locationPreference3_id) prefIds.add(String(obj.locationPreference3_id));

            // 2. Direct names
            if (obj.locationPreference1) prefNames.add(String(obj.locationPreference1).toLowerCase());
            if (obj.locationPreference2) prefNames.add(String(obj.locationPreference2).toLowerCase());
            if (obj.locationPreference3) prefNames.add(String(obj.locationPreference3).toLowerCase());

            // 3. Nested array: locationPreferences
            const arrayPrefs = obj.locationPreferences || [];
            if (Array.isArray(arrayPrefs)) {
                arrayPrefs.forEach((l: any) => {
                    if (l.locationId) prefIds.add(String(l.locationId));
                    if (l.location_id) prefIds.add(String(l.location_id));
                    if (l.id) prefIds.add(String(l.id));
                    if (l.name) prefNames.add(String(l.name).toLowerCase());
                });
            }
        };

        // Process all potential registration objects
        processRegistrationObj(currentRegistration);
        processRegistrationObj(currentRequest);

        const userRequestsList = Array.isArray(userRequests)
            ? userRequests
            : (Array.isArray(userRequests?.requests) ? userRequests.requests : []);

        const activeRequest = userRequestsList.find((req: any) =>
            String(req.referenceNumber) === String(currentRegistration?.referenceNumber) ||
            String(req.registrationId) === String(currentRegistration?.registrationId) ||
            String(req.id) === String(currentRegistration?.registrationId)
        ) || userRequestsList[0];

        processRegistrationObj(activeRequest);

        const currentUserRow = apiData.find((item: any) =>
            item.employeeNumber === employeeDetails.employeeNumber ||
            item.employeeId === employeeDetails.employeeNumber
        );
        processRegistrationObj(currentUserRow);

        const filtered = masterLocs.filter((loc: any) =>
            prefIds.has(String(loc.id)) ||
            prefNames.has(String(loc.name).toLowerCase())
        );

        if (!isRegistered) {
            return [];
        }
        return filtered;
    }, [masterLocs, currentRegistration, currentRequest, userRequests, apiData, employeeDetails.employeeNumber, isRegistered]);

    const eligibleQuarterTypeName = useMemo(() => {
        if (!employeeDetails.quarterTypeEligibility) return "";
        return (employeeDetails.quarterTypeEligibility.quarterType || "")
            .replace(/\s*accommodation\s*/gi, "");
    }, [employeeDetails.quarterTypeEligibility]);

    // Find the code corresponding to the user's eligible quarter type name
    const eligibleQuarterTypeCode = useMemo(() => {
        if (!eligibleQuarterTypeName || quarterTypes.length === 0) return "";
        const found = quarterTypes.find(t =>
            String(t.name || "").toLowerCase().includes(eligibleQuarterTypeName.toLowerCase()) ||
            String(t.code || "").toLowerCase() === eligibleQuarterTypeName.toLowerCase()
        );
        return found ? found.code : eligibleQuarterTypeName;
    }, [eligibleQuarterTypeName, quarterTypes]);

    const [selectedLocation, setSelectedLocation] = useState<string>("");
    const [searchTerm, setSearchTerm] = useState("");
    const [pools, setPools] = useState<any[]>([]);
    const [selectedPoolId, setSelectedPoolId] = useState<string>("");
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<any>(null);
    const [isGenerating, setIsGenerating] = useState(false);

    // Initial load: Fetch employee profile, master quarter types, pools, master locations and user requests
    useEffect(() => {
        dispatch(getEmployeeDetailsForStaffQuarter({}));
        dispatch(getStaffQuarterCurrentRegistration({}));
        dispatch(getStaffQuarterLocations({}));
        dispatch(getUserStaffQuarterRequests({}));
        dispatch(getStaffQuarterQuarterTypes({})).then((res: any) => {
            if (res.payload?.success) {
                setQuarterTypes(res.payload.data || []);
            }
        });
        dispatch(getStaffQuarterPools({})).then((res: any) => {
            if (res.payload?.success) {
                setPools(res.payload.data || []);
            }
        });
    }, [dispatch]);

    // Fetch detailed registration details once currentRegistration has loaded
    useEffect(() => {
        const regId = currentRegistration?.registrationId || currentRegistration?.id;
        if (regId) {
            dispatch(getStaffQuarterDetails({ id: regId }));
        }
    }, [currentRegistration?.registrationId, currentRegistration?.id, dispatch]);

    // Pre-select the first eligible location when locations load
    useEffect(() => {
        if (eligibleLocations.length > 0 && !selectedLocation) {
            setSelectedLocation(eligibleLocations[0].id?.toString() || "");
        }
    }, [eligibleLocations, selectedLocation]);

    const loggedInUserPoolInfo = useMemo(() => {
        const userEmpNo = String(employeeDetails?.employeeNumber || "").trim();
        let userRow = null;
        if (userEmpNo && Array.isArray(apiData) && apiData.length > 0) {
            userRow = apiData.find((item: any) => {
                const empNo = String(item.employeeNumber || item.employeeId || item.empId || item.empNo || "").trim();
                return empNo === userEmpNo;
            });
        }

        const id =
            getPoolId(currentRegistration) ||
            getPoolId(employeeDetails) ||
            getPoolId(currentRequest) ||
            getPoolId(userRow);

        const name =
            getPoolName(currentRegistration) ||
            getPoolName(employeeDetails) ||
            getPoolName(currentRequest) ||
            getPoolName(userRow);

        return { name: name.trim(), id: id.trim() };
    }, [apiData, employeeDetails, currentRegistration, currentRequest]);

    const effectivePoolId = useMemo(() => {
        if (selectedPoolId && selectedPoolId !== "All Pools") return selectedPoolId;
        const regPoolId = getPoolId(currentRegistration) || getPoolId(employeeDetails) || getPoolId(currentRequest);
        if (regPoolId) return regPoolId;
        if (loggedInUserPoolInfo.id) return loggedInUserPoolInfo.id;
        if (loggedInUserPoolInfo.name && Array.isArray(pools) && pools.length > 0) {
            const found = pools.find(p =>
                String(p.name || p.poolName || p.type || "").toLowerCase() === loggedInUserPoolInfo.name.toLowerCase()
            );
            if (found) return String(found.id || found.poolTypeId || found.pool_type_id || "");
        }
        return "";
    }, [selectedPoolId, loggedInUserPoolInfo, pools, currentRegistration, employeeDetails, currentRequest]);

    const fetchData = (
        page = pagination.current,
        limit = pagination.pageSize,
        location = selectedLocation,
        search = searchTerm,
        pool = effectivePoolId
    ) => {
        if (!eligibleQuarterTypeCode || !location) return;

        setIsLoading(true);

        const categoryVal = employeeDetails?.category || currentRegistration?.category || currentRequest?.category || "UR";
        const poolTypeIdVal =
            getPoolId(currentRegistration) ||
            getPoolId(employeeDetails) ||
            getPoolId(currentRequest) ||
            effectivePoolId;

        const shortQuarterType = (eligibleQuarterTypeName || "")
            .replace(/^Type-/i, "")
            .replace(/\s*accommodation\s*/gi, "")
            .trim() || "III";
        const qCode = isNaN(Number(eligibleQuarterTypeCode)) ? eligibleQuarterTypeCode : Number(eligibleQuarterTypeCode);
        const locId = isNaN(Number(location)) ? location : Number(location);
        const activePoolId = pool || poolTypeIdVal || effectivePoolId;
        const pTypeId = activePoolId ? (isNaN(Number(activePoolId)) ? activePoolId : Number(activePoolId)) : undefined;

        const payload: any = {
            category: categoryVal,
            limit: limit,
            locationId: locId,
            page: page,
            ...(pTypeId !== undefined && pTypeId !== "" ? { poolTypeId: pTypeId } : {}),
            quarterType: shortQuarterType,
            quarterTypeCode: qCode,
            quarter_type_code: qCode,
            ...(search && search.trim() !== "" ? {
                search: search.trim(),
                empId: search.trim(),
                employeeNumber: search.trim(),
                empName: search.trim(),
                employeeName: search.trim()
            } : {})
        };

        dispatch(getStaffQuarterPriorityList(payload)).then((res: any) => {
            if (res.payload?.success && res.payload.data) {
                const resultData = res.payload.data;
                let list = resultData.list || resultData.data || [];
                // Filter out records where willingness is already approved
                list = list.filter((item: any) => item.isWillingnessApproved !== true && item.isWillingnessApproved !== "true" && item.isWillingnessApproved !== 1);

                // Backup client-side filter for pool if API doesn't support it directly
                if (activePoolId && activePoolId !== "All Pools") {
                    list = list.filter((item: any) => {
                        const itemPoolId = item.poolTypeId || item.pool_type_id || item.poolId || item.pool_id || item.pool?.id;
                        const itemPoolName = getPoolName(item).trim();
                        if (itemPoolId && String(itemPoolId) === String(activePoolId)) return true;
                        if (loggedInUserPoolInfo.name && itemPoolName && itemPoolName.toLowerCase() === loggedInUserPoolInfo.name.toLowerCase()) return true;
                        return false;
                    });
                }
                setApiData(list);

                const total = resultData.pagination?.totalCount ||
                    resultData.pagination?.lolalCount ||
                    resultData.total ||
                    resultData.totalCount ||
                    0;

                setPagination(prev => ({
                    ...prev,
                    current: page,
                    pageSize: limit,
                    total: total
                }));
            }
            setIsLoading(false);
        }).catch(() => {
            setIsLoading(false);
        });
    };

    // Trigger data fetch when dependencies are resolved
    useEffect(() => {
        if (eligibleQuarterTypeCode && selectedLocation) {
            fetchData(1, pagination.pageSize, selectedLocation, searchTerm, effectivePoolId);
        }
    }, [eligibleQuarterTypeCode, selectedLocation, effectivePoolId, eligibleQuarterTypeName, employeeDetails?.category]);

    const handlePagination = (newPagination: any) => {
        fetchData(newPagination.current, newPagination.pageSize, selectedLocation, searchTerm, selectedPoolId);
    };

    const handleConfirmWillingness = () => {
        if (!selectedRecord) return;
        setIsGenerating(true);
        dispatch(generateStaffQuarterWillingness({
            registrationId: Number(selectedRecord.key),
            locationCode: selectedRecord.locationCode
        })).then((res: any) => {
            if (res.payload?.success || res.payload?.status === 200) {
                toast.success(res.payload.message || "Willingness generated successfully");
                setIsConfirmModalOpen(false);
                fetchData(); // Refresh list to reflect changes
            } else {
                const errorMsg = typeof res.payload === 'string' ? res.payload : (res.payload?.message || "Failed to generate willingness");
                toast.error(errorMsg);
            }
            setIsGenerating(false);
        }).catch(() => {
            setIsGenerating(false);
            toast.error("An error occurred");
        });
    };

    const formatDateOnly = (val: any) => {
        if (!val || val === "-" || val === "N/A") return "-";
        if (typeof val === 'string' && /^\d{2}-\d{2}-\d{2,4}$/.test(val)) return val;
        const d = new Date(val);
        if (isNaN(d.getTime())) return val;
        const day = String(d.getDate()).padStart(2, "0");
        const month = d.toLocaleString('default', { month: 'short' });
        const year = d.getFullYear();
        return `${day} ${month}, ${year}`;
    };

    const getRetirementDate = (dobStr: any) => {
        if (!dobStr || dobStr === "-" || dobStr === "N/A") return null;
        const d = new Date(dobStr);
        if (isNaN(d.getTime())) return null;
        const year = d.getFullYear() + 60;
        const month = d.getMonth();
        const lastDay = new Date(year, month + 1, 0);
        return lastDay.toISOString();
    };


    const filteredApiData = useMemo(() => {
        if (!Array.isArray(apiData) || apiData.length === 0) return [];

        let list = apiData;

        if (loggedInUserPoolInfo.name || loggedInUserPoolInfo.id) {
            list = list.filter((item: any) => {
                const itemPoolName = getPoolName(item).trim();
                const itemPoolId = getPoolId(item).trim();

                if (loggedInUserPoolInfo.id && itemPoolId) {
                    if (loggedInUserPoolInfo.id === itemPoolId) return true;
                }
                if (loggedInUserPoolInfo.name && itemPoolName) {
                    if (loggedInUserPoolInfo.name.toLowerCase() === itemPoolName.toLowerCase()) return true;
                }
                return false;
            });
        }

        if (searchTerm && searchTerm.trim() !== "") {
            const term = searchTerm.trim().toLowerCase();
            list = list.filter((item: any) => {
                const empNo = String(item.employeeNumber || item.employeeId || item.empId || item.empNo || "").toLowerCase();
                const empName = String(item.employeeName || item.name || item.empName || "").toLowerCase();
                const designation = String(item.designation || "").toLowerCase();
                const refNo = String(item.referenceNumber || item.registrationNo || item.registrationNumber || "").toLowerCase();
                return empNo.includes(term) || empName.includes(term) || designation.includes(term) || refNo.includes(term);
            });
        }

        return list;
    }, [apiData, loggedInUserPoolInfo, searchTerm]);

    const data: PriorityRecord[] = filteredApiData.map((item: any, idx: number) => {
        let prefs = [item.locationPreference1, item.locationPreference2, item.locationPreference3].filter(p => p && p !== "N/A" && p !== "-");
        if (prefs.length === 0 && item.location && item.location !== "N/A" && item.location !== "-") {
            prefs = [item.location];
        }

        // Resolve willingness details based on selected location preference
        let isWillingnessGenerated = false;
        let willingnessReferenceNumber = null;
        let willingnessStatus = null;

        const activeLocId = selectedLocation ? Number(selectedLocation) : null;
        if (activeLocId) {
            if (item.locationPreference1Id === activeLocId) {
                isWillingnessGenerated = !!(item.location1IsWillingnessGenerated || item.isWillingnessGeneratedForLoc1);
                willingnessReferenceNumber = item.location1WillingnessReferenceNumber;
                willingnessStatus = item.location1WillingnessStatus;
            } else if (item.locationPreference2Id === activeLocId) {
                isWillingnessGenerated = !!(item.location2IsWillingnessGenerated || item.isWillingnessGeneratedForLoc2);
                willingnessReferenceNumber = item.location2WillingnessReferenceNumber;
                willingnessStatus = item.location2WillingnessStatus;
            } else if (item.locationPreference3Id === activeLocId) {
                isWillingnessGenerated = !!(item.location3IsWillingnessGenerated || item.isWillingnessGeneratedForLoc3);
                willingnessReferenceNumber = item.location3WillingnessReferenceNumber;
                willingnessStatus = item.location3WillingnessStatus;
            }
        } else {
            isWillingnessGenerated = !!(
                item.location1IsWillingnessGenerated || item.isWillingnessGeneratedForLoc1 ||
                item.location2IsWillingnessGenerated || item.isWillingnessGeneratedForLoc2 ||
                item.location3IsWillingnessGenerated || item.isWillingnessGeneratedForLoc3 ||
                item.isWillingnessGenerated
            );
            willingnessReferenceNumber = item.location1WillingnessReferenceNumber ||
                item.location2WillingnessReferenceNumber ||
                item.location3WillingnessReferenceNumber ||
                item.willingnessReferenceNumber;
            willingnessStatus = item.location1WillingnessStatus ||
                item.location2WillingnessStatus ||
                item.location3WillingnessStatus ||
                item.willingnessStatus;
        }

        const dob = item.dateOfBirth || item.dob || item.date_of_birth || null;
        const doj = item.dateOfJoining || item.doj || item.date_of_joining || null;
        const rawPriority = parseInt(item.priority?.replace("#", "") || "0", 10);

        return {
            key: item.id?.toString() || idx.toString(),
            priorityNo: rawPriority > 0 ? rawPriority : (idx + 1),
            empNo: item.employeeNumber || item.empId || item.employeeId || "",
            name: item.employeeName || item.name || item.empName || "",
            designation: item.designation || "",
            department: item.department || item.dept || "Operations",
            regDate: item.registrationTimestamp || item.registrationDate || item.createdAt || "",
            category: resolveFlatCategory(item) || item.category || "UR",
            status: item.willingnessStatus || item.status || "",
            quarterType: (item.quarterType || item.quarter_type || "").split(" ")[0],
            location: prefs,
            locationPreference1: item.locationPreference1 || "-",
            locationPreference2: item.locationPreference2 || "-",
            locationPreference3: item.locationPreference3 || "-",
            locationCode: item.locationCode || item.location || item.locationPreference1 || null,
            willingnessReferenceNumber: willingnessReferenceNumber || null,
            willingnessStatus: willingnessStatus || null,
            isWillingnessGenerated: isWillingnessGenerated,
            registrationReferenceNumber: item.referenceNumber || item.registrationNo || item.registrationNumber || "N/A",
            pool: item.poolType || item.pool_type || item.pool?.name || item.poolName || item.pool_name || item.pool?.namePool || item.pool || "-",
            dateOfBirth: dob || "-",
            dateOfJoining: doj || "-",
            begda: item.begda || item.begDate || item.beginDate || item.startDate || item.payDate || doj || "-",
            endDate: item.endDate || item.end_date || item.expiryDate || (dob ? getRetirementDate(dob) : null) || "-"
        };
    });

    const columns = [
        {
            title: "Priority #",
            dataIndex: "priorityNo",
            key: "priorityNo",
            className: "text-center",
            render: (val: any) => <span className="fw-bold text-primary">#{val}</span>,
            sorter: (a: any, b: any) => a.priorityNo - b.priorityNo
        },
        {
            title: "Employee",
            dataIndex: "name",
            key: "name",
            render: (val: any, record: PriorityRecord) => {
                const isCurrentUser = record.empNo === employeeDetails.employeeNumber;
                return (
                    <div>
                        <div className={`fw-bold ${isCurrentUser ? "text-danger" : "text-dark"}`}>
                            {val} {isCurrentUser && <span className="badge bg-danger-light text-danger ms-1">You</span>}
                        </div>
                        <div className="text-muted small">{record.empNo} | {record.designation}</div>
                    </div>
                );
            },
            sorter: (a: any, b: any) => a.name.localeCompare(b.name)
        },
        {
            title: "DOB",
            dataIndex: "dateOfBirth",
            key: "dateOfBirth",
            render: (val: any) => <span className="text-dark" style={{ fontSize: '13px' }}>{formatDateOnly(val)}</span>
        },
        {
            title: "DOJ",
            dataIndex: "dateOfJoining",
            key: "dateOfJoining",
            render: (val: any) => <span className="text-dark" style={{ fontSize: '13px' }}>{formatDateOnly(val)}</span>
        },
        {
            title: "Quarter Type",
            dataIndex: "quarterType",
            key: "quarterType",
            render: (val: any) => <span className="badge bg-light text-dark border">{val}</span>
        },
        {
            title: "Category",
            dataIndex: "category",
            key: "category",
            render: (val: any) => <span className="badge bg-light text-dark border">{val || "UR"}</span>
        },
        {
            title: "Pool",
            dataIndex: "pool",
            key: "pool",
            render: (val: any) => <span className="badge bg-outline-secondary border text-secondary" style={{ fontSize: '12px' }}>{val}</span>
        },
        {
            title: "Preference 1",
            dataIndex: "locationPreference1",
            key: "locationPreference1",
            render: (val: any) => <span className="fw-medium text-dark" style={{ fontSize: '12.5px' }}>{val || "-"}</span>
        },
        {
            title: "Preference 2",
            dataIndex: "locationPreference2",
            key: "locationPreference2",
            render: (val: any) => <span className="text-muted" style={{ fontSize: '12.5px' }}>{val || "-"}</span>
        },
        {
            title: "Preference 3",
            dataIndex: "locationPreference3",
            key: "locationPreference3",
            render: (val: any) => <span className="text-muted" style={{ fontSize: '12.5px' }}>{val || "-"}</span>
        },
        {
            title: "BEGDA",
            dataIndex: "begda",
            key: "begda",
            render: (val: any) => <span className="text-dark" style={{ fontSize: '13px' }}>{formatDateOnly(val)}</span>
        },
        {
            title: "End Date",
            dataIndex: "endDate",
            key: "endDate",
            render: (val: any) => <span className="text-dark" style={{ fontSize: '13px' }}>{formatDateOnly(val)}</span>
        },
        {
            title: "Registration Date",
            dataIndex: "regDate",
            key: "regDate",
            render: (val: any) => {
                const d = new Date(val);
                const day = String(d.getDate()).padStart(2, "0");
                const month = d.toLocaleString('default', { month: 'short' });
                const year = d.getFullYear();
                const hours = String(d.getHours()).padStart(2, "0");
                const minutes = String(d.getMinutes()).padStart(2, "0");

                return (
                    <div className="d-flex flex-column">
                        <span className="text-dark fw-medium" style={{ fontSize: '13px' }}>
                            <i className="ti ti-calendar-event me-1 text-muted"></i>
                            {`${day} ${month}, ${year}`}
                        </span>
                        <span className="text-muted extra-small" style={{ fontSize: '11px' }}>
                            <i className="ti ti-clock me-1"></i>
                            {`${hours}:${minutes}`}
                        </span>
                    </div>
                );
            },
            sorter: (a: any, b: any) => new Date(a.regDate).getTime() - new Date(b.regDate).getTime()
        },
        {
            title: "Category",
            dataIndex: "category",
            key: "category",
            className: "text-center",
            render: (val: any) => <span className="badge bg-primary-light text-primary">{val}</span>
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            className: "text-center",
            render: (val: any) => {
                const status = val?.toUpperCase();
                const isSuccess = status === "ACTIVE" || status === "APPROVED" || status === "CONFIRMED" || status === "ACCEPTED";
                const isError = status === "REJECTED" || status === "DE-LISTED" || status === "CANCELLED" || status === "EXPIRED";

                return (
                    <span className={`badge ${isSuccess ? "bg-success-light text-success" : isError ? "bg-danger-light text-danger" : "bg-warning-light text-warning"}`}>
                        {val}
                    </span>
                );
            }
        },
        {
            title: "Willingness",
            key: "willingness",
            className: "text-center",
            render: (_: any, record: PriorityRecord) => (
                record.isWillingnessGenerated ? (
                    <div className="d-flex flex-column align-items-center justify-content-center">
                        <span className="text-success" title={`Willingness Ref: ${record.willingnessReferenceNumber || 'N/A'} (${record.willingnessStatus || 'PENDING'})`}>
                            <i className="ti ti-circle-check-filled fs-18"></i>
                        </span>
                        {record.willingnessReferenceNumber && (
                            <span className="text-muted mt-0.5 fw-medium" style={{ fontSize: '11px', letterSpacing: '0.2px' }}>
                                {record.willingnessReferenceNumber}
                            </span>
                        )}
                    </div>
                ) : (
                    <span className="text-muted">-</span>
                )
            )
        },
        {
            title: "Action",
            key: "action",
            className: "text-center",
            render: (_: any, record: any) => {
                const isCurrentUser = record.empNo === employeeDetails.employeeNumber;
                if (!isCurrentUser) return <span className="text-muted">-</span>;

                return (
                    <div className="d-flex align-items-center justify-content-center gap-2">
                        {!record.isWillingnessGenerated && (
                            <button
                                className="btn btn-sm btn-icon btn-outline-success border"
                                title="Generate Willingness"
                                onClick={() => {
                                    setSelectedRecord(record);
                                    setIsConfirmModalOpen(true);
                                }}
                            >
                                <i className="ti ti-checklist text-success"></i>
                            </button>
                        )}
                        <Link
                            to={routes.staffQuarter.details.replace(":id", encodeRouteParam(record.key))}
                            className="btn btn-sm btn-icon btn-light border"
                            title="View Details"
                        >
                            <i className="ti ti-eye text-primary"></i>
                        </Link>
                    </div>
                );
            }
        },
    ];

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                {/* Page Header */}
                <div className="page-header mb-4">
                    <div className="row align-items-center">
                        <div className="col">
                            <h3 className="page-title text-dark fw-bold d-flex align-items-center gap-2">
                                Eligible Priority List
                                {loggedInUserPoolInfo.name && (
                                    <span className="badge bg-primary-light text-primary fs-13 fw-medium">
                                        Pool: {loggedInUserPoolInfo.name}
                                    </span>
                                )}
                            </h3>
                            <ul className="breadcrumb bg-transparent p-0 mt-1">
                                <li className="breadcrumb-item">
                                    <Link to={routes.employeeDashboard}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item active">Priority List</li>
                            </ul>
                        </div>
                        <div className="col-auto">
                            <Link to={routes.staffQuarter.unwillingness} className="btn btn-outline-danger btn-sm rounded-pill px-3">
                                <i className="ti ti-ban me-1"></i> Unwillingness
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Info Card explaining eligibility filters */}
                {isRegistered ? (
                    <>
                        {/* FILTERS */}
                        <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '8px', background: '#ffffff' }}>
                            <div className="card-body p-2">
                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                                    <div className="d-flex flex-wrap align-items-center gap-2">
                                        <Select
                                            className="custom-select-ui-sm"
                                            style={{ width: '180px' }}
                                            showSearch
                                            optionFilterProp="children"
                                            value={selectedLocation || undefined}
                                            onChange={(value) => {
                                                setSelectedLocation(value || "");
                                                fetchData(1, pagination.pageSize, value || "", searchTerm, effectivePoolId);
                                            }}
                                            options={eligibleLocations.map((l: any) => ({ value: l.id?.toString(), label: l.name }))}
                                            placeholder="Location Preference"
                                            size="small"
                                        />

                                        <div className="input-group input-group-sm" style={{ width: '250px' }}>
                                            <span className="input-group-text bg-white border-end-0">
                                                <i className="ti ti-search text-muted" style={{ fontSize: '14px' }}></i>
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm border-start-0 ps-0"
                                                placeholder="Search Emp No. or Name..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        fetchData(1, pagination.pageSize, selectedLocation, searchTerm, effectivePoolId);
                                                    }
                                                }}
                                                style={{ fontSize: '12.5px', height: '32px' }}
                                            />
                                            {searchTerm && (
                                                <button
                                                    className="btn btn-sm btn-outline-secondary border-start-0 bg-white"
                                                    type="button"
                                                    onClick={() => {
                                                        setSearchTerm("");
                                                        fetchData(1, pagination.pageSize, selectedLocation, "", effectivePoolId);
                                                    }}
                                                    style={{ height: '32px' }}
                                                >
                                                    <i className="ti ti-x" style={{ fontSize: '12px' }}></i>
                                                </button>
                                            )}
                                        </div>

                                        <button
                                            className="btn btn-sm btn-primary shadow-sm d-inline-flex align-items-center justify-content-center"
                                            style={{ height: '32px', fontSize: '12px', borderRadius: '6px' }}
                                            onClick={() => {
                                                fetchData(1, pagination.pageSize, selectedLocation, searchTerm, effectivePoolId);
                                            }}
                                        >
                                            <i className="ti ti-search me-1"></i> Search
                                        </button>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <button
                                            className="btn btn-sm btn-danger shadow-sm d-inline-flex align-items-center justify-content-center"
                                            style={{ height: '32px', fontSize: '12px', borderRadius: '6px' }}
                                            onClick={() => {
                                                setSearchTerm("");
                                                setSelectedPoolId("");
                                                if (eligibleLocations.length > 0) {
                                                    const firstLoc = eligibleLocations[0].id?.toString() || "";
                                                    setSelectedLocation(firstLoc);
                                                    fetchData(1, pagination.pageSize, firstLoc, "", "");
                                                } else {
                                                    setSelectedLocation("");
                                                    fetchData(1, pagination.pageSize, "", "", "");
                                                }
                                            }}
                                        >
                                            <i className="ti ti-refresh me-1"></i> Reset
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* TABLE */}
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                            <div className="card-body p-0 py-3 custom-table-layout">
                                <Table
                                    columns={columns}
                                    dataSource={data}
                                    Selection={false}
                                    paginationConfig={pagination}
                                    handlePagination={handlePagination}
                                    loading={isLoading}
                                />
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="card border-0 shadow-sm p-4 text-center mb-4" style={{ borderRadius: '12px' }}>
                        <div className="py-4">
                            <i className="ti ti-notes-off text-muted mb-3" style={{ fontSize: '48px' }}></i>
                            <h5 className="fw-bold text-dark mb-2">No Active Registration Found</h5>
                            <p className="text-muted small mb-3">
                                You need to submit a staff quarter registration first to view the priority list for your preferred locations.
                            </p>
                            <Link to={routes.staffQuarter.newRegistration} className="btn btn-danger btn-sm px-4 fw-bold shadow-sm" style={{ borderRadius: '8px' }}>
                                Go to Registration
                            </Link>
                        </div>
                    </div>
                )}

                {/* Confirm Willingness Modal */}
                <Modal
                    title="Generate Willingness"
                    open={isConfirmModalOpen}
                    onOk={handleConfirmWillingness}
                    onCancel={() => setIsConfirmModalOpen(false)}
                    confirmLoading={isGenerating}
                    okText="Generate"
                    cancelText="Cancel"
                    zIndex={9999}
                >
                    <div className="py-3 text-center">
                        <i className="ti ti-info-circle text-primary mb-3" style={{ fontSize: '48px' }}></i>
                        <p className="mb-0 fs-16 fw-medium">
                            Are you sure you want to generate willingness for <br />
                            <span className="text-primary fw-bold">{selectedRecord?.name}</span>
                            {selectedRecord?.registrationReferenceNumber && (
                                <span className="text-muted small ms-2" title="EMP ID">
                                    (EMP ID: {selectedRecord.empNo})
                                </span>
                            )}
                        </p>
                        <p className="text-muted small mt-2">
                            This will initiate the willingness confirmation process for your staff quarter application.
                        </p>
                    </div>
                </Modal>

            </div>
            <style>{`
          .bg-success-light { background-color: rgba(25, 135, 84, 0.1); }
          .bg-danger-light { background-color: rgba(220, 53, 69, 0.1); }
          .bg-info-light { background-color: rgba(13, 202, 240, 0.1); }
          .bg-primary-light { background-color: rgba(var(--bs-primary-rgb), 0.1); }
          .custom-table-layout {
              display: flex;
              flex-direction: column;
          }
          .custom-table-layout .table-top-data {
              order: 2;
              margin-top: 5px;
              padding: 0 15px;
          }
          .custom-table-layout .table.datanew {
              order: 1;
          }
          .custom-table-layout .dataTables_filter {
              display: none !important;
          }
          .custom-table-layout .dataTables_length {
              justify-content: flex-end;
          }
          .custom-table-layout .table-top-data .row {
              flex-direction: row-reverse;
          }
          .custom-select-ui .ant-select-selector {
              border-radius: 8px !important;
              border-color: #e2e8f0 !important;
              box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
          }
          .custom-select-ui .ant-select-selector:hover {
              border-color: #cbd5e1 !important;
          }
          .custom-select-ui-sm .ant-select-selector {
              border-radius: 6px !important;
              height: 32px !important;
              padding: 0 8px !important;
              display: flex;
              align-items: center;
              border-color: #e2e8f0 !important;
              font-size: 12.5px !important;
              box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
          }
          .custom-select-ui-sm .ant-select-selector:hover {
              border-color: #cbd5e1 !important;
          }
          .custom-select-ui-sm .ant-select-selection-item, 
          .custom-select-ui-sm .ant-select-selection-placeholder {
              line-height: 30px !important;
          }
      `}</style>
        </div>
    );
};

export default EmployeePriorityList;
